import { useEffect, useRef, useId } from 'react';
import Icon from './Icon';

// ─── MODAL COMPONENT ──────────────────────────────────────────────────────────
//
//  Reusable overlay dialog. Click the backdrop or the × button to close.
//  508 / WCAG 2.1 AA: role="dialog", aria-modal, aria-labelledby, focus trap,
//  Escape key to close, and focus restoration on dismiss.
//
//  Props:
//    open    {boolean}   controls visibility
//    onClose {function}  called when user dismisses the modal
//    title   {string}    heading text
//    width   {number}    max-width in px (default 560)
//    children            modal body content
//

const Modal = ({ open, onClose, title, children, width = 560 }) => {
  const titleId          = useId();
  const panelRef         = useRef(null);
  const previousFocusRef = useRef(null);

  // Move focus into the dialog on open; restore it on close.
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
      const raf = requestAnimationFrame(() => {
        const first = panelRef.current?.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        (first || panelRef.current)?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [open]);

  // Tab trap + Escape to close.
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      panelRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []
    ).filter(el => el.offsetParent !== null);

    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border2)',
          borderRadius: 'var(--radius-xl)',
          width: '100%', maxWidth: width,
          maxHeight: '90vh', overflow: 'auto',
          boxShadow: 'var(--shadow)',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid var(--border)',
        }}>
          <h3 id={titleId} style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="modal-close-btn"
            style={{
              background: 'var(--bg3)', border: 'none', color: 'var(--text2)',
              borderRadius: 8, width: 36, height: 36, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name="close" size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

export default Modal;
